import { DEFAULT_PROJECT_ID } from '@/constants';
import { commentSchema } from '@/models/commentSchema';
import { mapOverlaySchema } from '@/models/mapOverlaySchema';
import { mapModelSchema } from '@/models/modelSchema';
import { projectSchema } from '@/models/projectSchema';
import { userPrivilegeSchema } from '@/models/userPrivilegesSchema';
import { axiosInstance, axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { OverviewImageView, Project } from '@/types/models';
import { ThunkConfig } from '@/types/store';
import { getError } from '@/utils/error-report/getError';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { z } from 'zod';
import { apiPath } from '../apiPath';
import { PROJECT_FETCHING_ERROR_PREFIX } from './project.constants';
import { SetProjectIdParams } from './project.types';

const DEFAULT_VALUE = 0;

export const getProjectById = createAsyncThunk<Project | undefined, string, ThunkConfig>(
  'project/getProjectById',
  async id => {
    try {
      const response = await axiosInstanceNewAPI.get<Project>(apiPath.getProjectById(id));

      const isDataValid = validateDataUsingZodSchema(response.data, projectSchema);

      return isDataValid ? response.data : undefined;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PROJECT_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const getProjectId = createAsyncThunk<Project | undefined, string, ThunkConfig>(
  'project/getProjectId',
  async id => {
    try {
      const response = await axiosInstance.get<Project>(apiPath.getProjectById(id));

      const isDataValid = validateDataUsingZodSchema(response.data, projectSchema);

      return isDataValid ? response.data : undefined;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PROJECT_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const setProjectId = createAsyncThunk<string, SetProjectIdParams, ThunkConfig>(
  'project/setProjectId',
  async ({ queryData }) => {
    return queryData?.id || DEFAULT_PROJECT_ID;
  },
);

export interface UpdateProjectData {
  name: string;
  disease?: string;
  organism?: string;
  version?: string;
  notifyEmail?: string;
  sbgnFormat: boolean;
  licenseId?: string;
  customLicenseName?: string;
  customLicenseUrl?: string;
}

export const updateProject = createAsyncThunk<
  Project,
  { projectId: string; projectData: UpdateProjectData },
  ThunkConfig
>('project/updateProject', async ({ projectId, projectData }, { rejectWithValue }) => {
  try {
    const payload = {
      project: {
        name: projectData.name,
        version: projectData.version || '',
        notifyEmail: projectData.notifyEmail || '',
        // Format organism and disease as MIRIAM annotations matching old admin
        organism: projectData.organism ? { type: 'TAXONOMY', resource: projectData.organism } : {},
        disease: projectData.disease ? { type: 'MESH_2012', resource: projectData.disease } : {},
        // Handle license data - explicitly send null to clear license
        ...(projectData.licenseId && projectData.licenseId !== 'OTHER'
          ? { license: { id: parseInt(projectData.licenseId, 10) } }
          : { license: null }),
        // Always include custom license fields - empty string clears them in backend
        customLicenseName:
          projectData.customLicenseName !== undefined ? projectData.customLicenseName : '',
        customLicenseUrl:
          projectData.customLicenseUrl !== undefined ? projectData.customLicenseUrl : '',
      },
    };

    const response = await axiosInstance.patch(apiPath.updateProject(projectId), payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const parsedId = parseInt(projectId, 10);
    const safeId = Number.isNaN(parsedId) ? DEFAULT_VALUE : parsedId;
    let safeOverviewImageViews: OverviewImageView[] = [];
    if (Array.isArray(response.data.overviewImageViews)) {
      safeOverviewImageViews = response.data.overviewImageViews.filter(
        (view: unknown) => view && typeof (view as { id: number }).id === 'number',
      ) as OverviewImageView[];
    }

    const safeTopOverviewImage =
      response.data.topOverviewImage && typeof response.data.topOverviewImage.id === 'number'
        ? response.data.topOverviewImage
        : null;

    const transformedData: Project = {
      id: response.data.idObject || safeId,
      name: response.data.name || '',
      version: response.data.version || '',
      notifyEmail: response.data.notifyEmail || '',
      disease: response.data.disease || null,
      diseaseName: response.data.disease?.resource || '',
      organism: response.data.organism || null,
      organismName: response.data.organism?.resource || '',
      owner:
        typeof response.data.owner === 'string'
          ? { login: response.data.owner }
          : response.data.owner || { login: '' },
      topMap: response.data.topMap || { id: DEFAULT_VALUE, name: '' },
      sbgnFormat: response.data.sbgnFormat !== undefined ? response.data.sbgnFormat : false,
      status: response.data.status || '',
      directory: response.data.directory || '',
      progress: response.data.progress || DEFAULT_VALUE,
      logEntries: response.data.logEntries !== undefined ? response.data.logEntries : false,
      sharedInMinervaNet:
        response.data.sharedInMinervaNet !== undefined ? response.data.sharedInMinervaNet : false,
      projectId: response.data.projectId || `${projectId}`,
      creationDate: response.data.creationDate || new Date().toISOString(),
      overviewImageViews: safeOverviewImageViews,
      topOverviewImage: safeTopOverviewImage,
      license: response.data.license || null,
      customLicenseName: response.data.customLicenseName || null,
      customLicenseUrl: response.data.customLicenseUrl || null,
    };

    const isDataValid = validateDataUsingZodSchema(transformedData, projectSchema.partial());
    if (!isDataValid) {
      throw new Error('Invalid project data received from server');
    }

    return transformedData;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: { data?: unknown; status?: number; headers?: unknown };
      };
      const errorData = axiosError.response?.data as { message?: string };
      return rejectWithValue(errorData?.message || 'Failed to update project');
    }
    if (error && typeof error === 'object' && 'request' in error) {
      return rejectWithValue('No response received from server');
    }
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to update project');
  }
});

export interface UpdateMapData {
  name: string;
  description?: string;
}

export interface MapModel {
  id: number;
  name: string;
  description?: string;
}

export const updateMap = createAsyncThunk<
  MapModel,
  { projectId: string; mapId: number; mapData: UpdateMapData },
  ThunkConfig
>('project/updateMap', async ({ projectId, mapId, mapData }, { rejectWithValue }) => {
  try {
    const payload = {
      model: {
        name: mapData.name,
        description: mapData.description,
      },
    };

    const response = await axiosInstance.patch(apiPath.updateMap(projectId, mapId), payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const isDataValid = validateDataUsingZodSchema(response.data, mapModelSchema);
    if (!isDataValid) {
      throw new Error('Invalid map data received from server');
    }

    return response.data;
  } catch (error) {
    return rejectWithValue('Failed to update map');
  }
});

export const getProjectMaps = createAsyncThunk<MapModel[], string, ThunkConfig>(
  'project/getProjectMaps',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(apiPath.getProjectMaps(projectId));

      const isDataValid = validateDataUsingZodSchema(response.data, z.array(mapModelSchema));
      if (!isDataValid) {
        throw new Error('Invalid maps data received from server');
      }

      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch project maps');
    }
  },
);

export interface Overlay {
  id: number;
  name: string;
  description?: string;
  publicOverlay: boolean;
  type: string;
}

export const getProjectOverlays = createAsyncThunk<Overlay[], string, ThunkConfig>(
  'project/getProjectOverlays',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(apiPath.getProjectOverlays(projectId));

      const isDataValid = validateDataUsingZodSchema(response.data, z.array(mapOverlaySchema));
      if (!isDataValid) {
        throw new Error('Invalid overlays data received from server');
      }

      return response.data || [];
    } catch (error) {
      return rejectWithValue('Failed to fetch project overlays');
    }
  },
);

export interface Comment {
  id: number;
  content: string;
  title?: string;
  elementId?: string;
  modelId?: number;
  owner?: string;
  type?: string;
}

export const getProjectComments = createAsyncThunk<
  Comment[],
  { projectId: string; modelId?: string },
  ThunkConfig
>('project/getProjectComments', async ({ projectId, modelId = '*' }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(apiPath.getProjectComments(projectId, modelId));

    const isDataValid = validateDataUsingZodSchema(response.data, z.array(commentSchema));
    if (!isDataValid) {
      throw new Error('Invalid comments data received from server');
    }

    return response.data || [];
  } catch (error) {
    return rejectWithValue('Failed to fetch project comments');
  }
});

export interface PrivilegeResponse {
  success: boolean;
  message?: string;
}

export const grantProjectPrivileges = createAsyncThunk<
  PrivilegeResponse,
  { projectId: string; login: string; privileges: string[] },
  ThunkConfig
>(
  'project/grantProjectPrivileges',
  async ({ projectId, login, privileges }, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceNewAPI.post(
        apiPath.grantProjectPrivileges(projectId),
        {
          login,
          privileges,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      const isDataValid = validateDataUsingZodSchema(response.data, userPrivilegeSchema);
      if (!isDataValid) {
        throw new Error('Invalid privilege data received from server');
      }

      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to grant project privileges');
    }
  },
);

export const revokeProjectPrivileges = createAsyncThunk<
  PrivilegeResponse,
  { projectId: string; login: string; privileges: string[] },
  ThunkConfig
>(
  'project/revokeProjectPrivileges',
  async ({ projectId, login, privileges }, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceNewAPI.post(
        apiPath.revokeProjectPrivileges(projectId),
        {
          login,
          privileges,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to revoke project privileges');
    }
  },
);
